import { useState } from "react";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { ExternalLink, Eye } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/context/AuthContext";
import type { Product } from "@/pages/LojaPage";

interface ProductCardProps {
  product: Product;
}

const categoryColors: Record<string, string> = {
  loja: "bg-blue-500 hover:bg-blue-600",
  planilha: "bg-green-500 hover:bg-green-600",
  curso: "bg-purple-500 hover:bg-purple-600",
  ebook: "bg-orange-500 hover:bg-orange-600",
  ferramenta: "bg-cyan-500 hover:bg-cyan-600",
};

export const ProductCard = ({ product }: ProductCardProps) => {
  const { user } = useAuth();
  const [open, setOpen] = useState(false);

  const handleProductClick = async () => {
    try {
      // Registrar clique para analytics
      await supabase.from("affiliate_product_clicks").insert({
        product_id: product.id,
        user_id: user?.id || null,
        user_agent: navigator.userAgent,
      });
    } catch (error) {
      console.error("Erro ao registrar clique:", error);
    }

    // Abrir link em nova aba
    window.open(product.affiliate_link, "_blank", "noopener,noreferrer");
  };


  return (
    <div className="group relative overflow-hidden rounded-xl border-2 border-primary/20 bg-gradient-to-br from-card to-accent/5 shadow-lg transition-all duration-300 hover:shadow-2xl hover:scale-[1.02] hover:border-primary/40 backdrop-blur-sm">
      {/* Imagem */}
      <div className="relative aspect-video overflow-hidden bg-muted">
        <img
          src={product.image_url}
          alt={product.title}
          className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-110"
          loading="lazy"
          onError={(e) => {
            (e.target as HTMLImageElement).src = "/placeholder.svg";
          }}
        />
        <div className="absolute top-3 right-3">
          <Badge
            className={`${
              categoryColors[product.category] || "bg-gray-500 hover:bg-gray-600"
            } text-white border-0 shadow-lg`}
          >
            {product.category}
          </Badge>
        </div>
      </div>

      {/* Conteúdo */}
      <div className="p-6 space-y-4">
        <h3 className="text-xl font-bold line-clamp-2 min-h-[3.5rem]">
          {product.title}
        </h3>
        <p className="text-muted-foreground line-clamp-3 min-h-[4.5rem]">
          {product.description}
        </p>
        {product.price_text && (
          <p className="text-primary font-semibold text-lg">
            {product.price_text}
          </p>
        )}
        <div className="grid grid-cols-2 gap-3">
          <Button variant="outline" onClick={() => setOpen(true)}>
            <Eye className="mr-2 h-4 w-4" />
            Ver mais
          </Button>
          <Button
            className="group-hover:bg-primary group-hover:text-primary-foreground transition-all duration-300"
            onClick={handleProductClick}
          >
            Visitar Loja
            <ExternalLink className="ml-2 h-4 w-4" />
          </Button>
        </div>
      </div>

      {/* Modal com descrição completa */}
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-lg max-h-[85vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="text-xl pr-6">{product.title}</DialogTitle>
            <DialogDescription className="sr-only">
              Detalhes do produto {product.title}
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4">
            <div className="relative aspect-video overflow-hidden rounded-lg bg-muted">
              <img
                src={product.image_url}
                alt={product.title}
                className="w-full h-full object-cover"
                loading="lazy"
                onError={(e) => {
                  (e.target as HTMLImageElement).src = "/placeholder.svg";
                }}
              />
            </div>

            <div className="flex items-center gap-2 flex-wrap">
              <Badge
                className={`${
                  categoryColors[product.category] || "bg-gray-500"
                } text-white border-0`}
              >
                {product.category}
              </Badge>
              {product.price_text && (
                <span className="text-primary font-semibold">
                  {product.price_text}
                </span>
              )}
            </div>

            <p className="text-muted-foreground whitespace-pre-line leading-relaxed">
              {product.description}
            </p>

            <Button className="w-full" onClick={handleProductClick}>
              Visitar Loja
              <ExternalLink className="ml-2 h-4 w-4" />
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Efeito de brilho no hover */}
      <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/10 to-transparent -translate-x-full group-hover:translate-x-full transition-transform duration-1000 pointer-events-none" />
    </div>

  );
};
